// ReviewBook.js
import React, { useState, useEffect } from 'react';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Box, Typography, Button, Paper, IconButton } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from '../axiosConfig';
import './ReviewBook.css';
import API_URL from '../config';

const ReviewBook = () => {
  const location = useLocation();
  const { bookId } = location.state || {};
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const navigate = useNavigate();

  function getPagesCount(curPages) {
    // define start page to count
    const startPage = 1;

    // Letzte belegte physische Seite finden
    let lastOccupiedPage = startPage;

    for (let i = 0; i < curPages.length; i++) {
      const [leftSide, rightSide] = curPages[i];
      const physicalLeft = i * 2;
      const physicalRight = i * 2 + 1;

      if (leftSide.length > 0) {
        lastOccupiedPage = Math.max(lastOccupiedPage, physicalLeft);
      }
      if (rightSide.length > 0) {
        lastOccupiedPage = Math.max(lastOccupiedPage, physicalRight);
      }
    }

    // Anzahl der gezählten Seiten (inkl. Impressum)
    let totalPagesBefore = lastOccupiedPage - startPage + 1;
    let multipleOf6Pages = totalPagesBefore;
    let emptyPagesToAdd = 0;

    // Prüfen, ob totalPages durch 6 teilbar ist
    if (totalPagesBefore % 6 !== 0) {
      multipleOf6Pages += 6 - (multipleOf6Pages % 6); // Auf die nächste durch 6 teilbare Zahl erhöhen
    }

    if (multipleOf6Pages !== totalPagesBefore) {
      emptyPagesToAdd = multipleOf6Pages - totalPagesBefore;
    }

    return { multipleOf6Pages, emptyPagesToAdd };
  }

  const loadBook = async (bookId) => {
    const token = localStorage.getItem('authToken');
    try {
      const response = await axiosInstance.get(`/books/${bookId}`, {
        headers: { Authorization: `${token}` }
      });
      const bookData = response.data;
  
      if (bookData.pages.length > 0) {
        const { multipleOf6Pages, emptyPagesToAdd } = getPagesCount(bookData.pages);
        const currentPages = bookData.pages || [];
  
        // Prüfe ob leere Doppelseiten hinzugefügt werden müssen
        let emptyDoublePagesToAdd = Math.ceil(emptyPagesToAdd / 2);
        for (let i = 0; i < emptyDoublePagesToAdd; i++) {
          currentPages.push([[], []]);
        }
  
        setPages(currentPages);
      }
  
    } catch (error) {
      console.error('Fehler beim Laden des Buches:', error);
      alert('Fehler beim Laden des Buches.');
    }
  };

  const getPageOffset = () => {
    let lastTocPageIndex = -1;
    let lastTocSideIndex = -1;
  
    for (let i = 0; i < pages.length; i++) {
      const [left, right] = pages[i];
  
      if (left.some((c) => c.name.startsWith("Inhaltsverzeichnis"))) {
        lastTocPageIndex = i;
        lastTocSideIndex = 0; // Linke Seite
      }
      if (right.some((c) => c.name.startsWith("Inhaltsverzeichnis"))) {
        lastTocPageIndex = i;
        lastTocSideIndex = 1; // Rechte Seite (überschreibt linke Seite, falls beides vorhanden)
      }
    }
  
    return lastTocPageIndex === -1 ? 4 : (lastTocPageIndex * 2 + lastTocSideIndex + 1);
  };

  const getLeftPageNumber = (currentPage) => {
    let pageOffset = getPageOffset();
    if (pageOffset % 2 === 1) {
      // Inhaltsverzeichnis hört auf der linken Seite auf, die rechte Seite daneben ist Seite Nr. 1
      return currentPage * 2 - getPageOffset() + 1;
    } 
    // Inhaltsverzeichnis hört auf der rechten Seite auf, d.h. es hat mehrere Seiten.
    // Auf der nächsten Doppelseite wird die linke Seite leer sein und keine Seitennummerierung haben
    // und die rechte Seite ist dann die Seite mit der Nr. 1
    return currentPage * 2 - getPageOffset();
  }

  const handleCheckout = () => {
    const {multipleOf6Pages, emptyPagesToAdd} = getPagesCount(pages);
    navigate('/pages/checkout', {
      state: { 
        bookId,
        pagesCount: multipleOf6Pages,
        emptyPagesToAdd: emptyPagesToAdd,
      }
    });
  };

  useEffect(() => {
    if (bookId) {
      loadBook(bookId);
    }
  }, [bookId]);

  const handleNextPage = () => {
    if (currentPage < pages.length - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <Box className="review-book-container">
      <Typography variant="h5" gutterBottom>
        Überprüfen Sie Ihr Schullogbuch
      </Typography>
      <Box className="double-page">
        {/* Linke Seite der Doppelseite */}
        <Paper className="book-page left-page" onClick={handlePreviousPage} disabled={currentPage === 0} elevation={3}>
        <IconButton aria-label="back" size="large">
          <ArrowBackIosNewIcon />
        </IconButton>
          {pages[currentPage]?.[0]?.map((component, index) => (
            <Box key={component.uniqueId} className="component">
              <img src={component.pngImageUrl} alt={component.name} className="full-page-image" />
            </Box>
          ))}
          {getLeftPageNumber(currentPage) > 0 && (
            <Typography fontSize="12px" className="page-number left-page-number">
              {getLeftPageNumber(currentPage)}
            </Typography>
          )}
        </Paper>

        {/* Rechte Seite der Doppelseite */}
        <Paper className="book-page right-page" onClick={handleNextPage} disabled={currentPage === pages.length - 1} elevation={3}>
        <IconButton aria-label="forward" size="large">
          <ArrowForwardIosIcon />
        </IconButton>
          {pages[currentPage]?.[1]?.map((component, index) => (
            <Box key={component.uniqueId} className="component">
              <img src={`${component.pngImageUrl}?t=${Date.now()}`} alt={component.name} className="full-page-image" />
            </Box>
          ))}
          {getLeftPageNumber(currentPage) + 1 > 0 && (
            <Typography fontSize="12px" className="page-number right-page-number">
              {getLeftPageNumber(currentPage) + 1}
            </Typography>
          )}
        </Paper>
      </Box>
      <Box className="quality-note">
        <Typography variant="body2">
          Hinweis: Die in dieser Web-Darstellung gezeigten Bilder können eventuelle Qualitätsverluste aufweisen, welche im gedruckten Buch nicht mehr sichtbar sein werden.
        </Typography>
      </Box>
        <Box className="bottom-wrapper">
          <Box className="uebersicht-button">
            <Button startIcon={<ArrowBackIosNewIcon />} variant="outlined" color="primary" href="/pages/configurator">
              Zu den Entwürfen
            </Button>
          </Box>
          <Box className="druckauftrag-button">
          <Button onClick={handleCheckout} variant="contained" color="primary">
            Zur Kostenübersicht
          </Button>
          </Box>
        </Box>
    </Box>
  );
};

export default ReviewBook;
